import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";

/**
 * Admin session.
 *
 * The access token is short-lived (30 minutes) and the refresh token rotates on
 * every use — presenting one twice fails, because a second presentation is what
 * a leak looks like. Both live here, and `adminHttp` handles the rotation.
 *
 * What is stored is a *cache of what the server granted*, never the authority.
 * Permissions are re-checked on every request server-side; the copy here only
 * decides which menu items to draw. Editing localStorage gets you a broken
 * console, not access.
 */

export interface AdminUser {
  id: string;
  email: string;
  full_name: string;
  is_superuser: boolean;
}

interface AuthState {
  accessToken: string | null;
  refreshToken: string | null;
  user: AdminUser | null;
  permissions: string[];

  signIn: (tokens: { access: string; refresh: string }) => void;
  setIdentity: (user: AdminUser, permissions: string[]) => void;
  setTokens: (access: string, refresh: string) => void;
  signOut: () => void;
}

export const useAuth = create<AuthState>()(
  persist(
    (set) => ({
      accessToken: null,
      refreshToken: null,
      user: null,
      permissions: [],

      signIn: ({ access, refresh }) => set({ accessToken: access, refreshToken: refresh }),
      setIdentity: (user, permissions) => set({ user, permissions }),
      setTokens: (access, refresh) => set({ accessToken: access, refreshToken: refresh }),
      signOut: () => set({ accessToken: null, refreshToken: null, user: null, permissions: [] }),
    }),
    {
      name: "ind.admin.v1",
      storage: createJSONStorage(() => localStorage),
      version: 1,
    },
  ),
);

/**
 * Does the signed-in user hold a permission?
 *
 * Mirrors the server's rule exactly, wildcards included: `"*"` grants
 * everything and `"crm.*"` grants a module. If the two ever disagree the menu
 * shows something the API then refuses, which reads to the user as a bug rather
 * than as a permission boundary.
 */
export function hasPermission(permissions: string[], code: string): boolean {
  if (permissions.includes("*")) return true;
  if (permissions.includes(code)) return true;
  const [module] = code.split(".");
  return permissions.includes(`${module}.*`);
}

/** Hook form of the same check, for gating a control inside a component. */
export function useCan(code: string): boolean {
  const permissions = useAuth((state) => state.permissions);
  return hasPermission(permissions, code);
}
